import type { LucideIcon } from 'lucide-react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card } from '@/shared/ui/card/Card';
import { cn } from '@/shared/lib/utils';

interface KpiCardProps {
  title: string;
  value: string;
  change?: number;
  icon: LucideIcon;
  variant?: 'primary' | 'secondary' | 'accent';
  className?: string;
  delay?: number;
}

export function KpiCard({
  title,
  value,
  change,
  icon: Icon,
  variant = 'primary',
  className,
  delay = 0,
}: KpiCardProps) {
  const isPositive = (change ?? 0) >= 0;

  const variantStyles = {
    primary: {
      iconBg: 'bg-primary-muted',
      iconColor: 'text-primary',
      accent: 'from-primary/0 via-primary/10 to-primary/0',
      glow: 'shadow-glow-primary',
    },
    secondary: {
      iconBg: 'bg-secondary-muted',
      iconColor: 'text-secondary',
      accent: 'from-secondary/0 via-secondary/10 to-secondary/0',
      glow: 'shadow-glow-secondary',
    },
    accent: {
      iconBg: 'bg-accent-muted',
      iconColor: 'text-accent',
      accent: 'from-accent/0 via-accent/10 to-accent/0',
      glow: 'shadow-[0_8px_30px_rgba(245,158,11,0.18)]',
    },
  };

  const styles = variantStyles[variant];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: 'easeOut' as const }}
    >
      <Card
        variant="interactive"
        padding="md"
        className={cn('relative overflow-hidden', styles.glow, className)}
      >
        <div
          className={cn(
            'absolute inset-x-4 top-3 h-1 rounded-full bg-gradient-to-r',
            styles.accent
          )}
        />

        <div className="flex items-center gap-4 pt-7">
          <div
            className={cn(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
              styles.iconBg
            )}
          >
            <Icon className={cn('h-5 w-5', styles.iconColor)} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-text-muted">
                {title}
              </p>
              {change !== undefined && (
                <span
                  className={cn(
                    'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold',
                    isPositive
                      ? 'bg-success-bg text-success-text'
                      : 'bg-danger-bg text-danger-text'
                  )}
                >
                  {isPositive ? (
                    <ArrowUpRight className="h-3 w-3" />
                  ) : (
                    <ArrowDownRight className="h-3 w-3" />
                  )}
                  {Math.abs(change).toFixed(1)}% vs mes ant.
                </span>
              )}
            </div>
            <p className="mt-2 truncate font-display text-xl font-bold tracking-tight text-text-primary sm:text-2xl">
              {value}
            </p>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
